import React from 'react';
import { parseISO, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import BaseField from './BaseField';

/**
 * DateField component for workflow forms.
 * Extends the BaseField with date picker functionality.
 */
const DateField = ({
  id,
  name,
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  disabled,
  fullWidth,
  className,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  ...rest
}) => {
  // Handle date change
  const handleDateChange = (date) => {
    onChange({
      target: {
        name,
        value: date,
      },
    });
  };

  return (
    <BaseField
      id={id}
      name={name}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      fullWidth={fullWidth}
      className={className}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={
            typeof value === 'string' && value
              ? (isValid(parseISO(value)) ? parseISO(value) : null)
              : value || null
          }
          onChange={handleDateChange}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          disablePast={disablePast}
          disableFuture={disableFuture}
          renderInput={(params) => (
            <TextField
              {...params}
              id={id}
              name={name}
              fullWidth={fullWidth}
              error={Boolean(error)}
              size="small"
              variant="outlined"
            />
          )}
          {...rest}
        />
      </LocalizationProvider>
    </BaseField>
  );
};

DateField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
};

DateField.defaultProps = {
  required: false,
  disabled: false,
  fullWidth: true,
  disablePast: false,
  disableFuture: false,
};

export default DateField;
